// src/App.jsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';

// ── Public pages ──────────────────────────────────────────────────────────────
const PublicLayout              = lazy(() => import('./components/public/PublicLayout'));
const HomePage                  = lazy(() => import('./pages/public/HomePage'));
const PackagesPage              = lazy(() => import('./pages/public/PackagesPage'));
const PackageDetailPage         = lazy(() => import('./pages/public/PackageDetailPage'));
const DomesticDestinationsPage  = lazy(() => import('./pages/public/DomesticDestinationsPage'));
const EnquiryPage               = lazy(() => import('./pages/public/EnquiryPage'));

// ── Admin pages ───────────────────────────────────────────────────────────────
const AdminLogin        = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout       = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard    = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminPackages     = lazy(() => import('./pages/admin/AdminPackages'));
const ItineraryMaker    = lazy(() => import('./pages/admin/ItineraryMaker'));
const AdminDestinations = lazy(() => import('./pages/admin/AdminDestinations'));
const AdminEnquiries    = lazy(() => import('./pages/admin/AdminEnquiries'));
const AdminSettings     = lazy(() => import('./pages/admin/AdminSettings'));
const ProtectedRoute    = lazy(() => import('./components/admin/ProtectedRoute'));

// ── Spinner ───────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-900 rounded-full animate-spin mx-auto mb-3" />
      <p className="text-gray-400 text-sm">Loading…</p>
    </div>
  </div>
);

// ── Simple coming-soon placeholder ────────────────────────────────────────────
const ComingSoon = ({ title }) => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-center">
      <div className="text-5xl mb-4">🚧</div>
      <h2 className="text-xl font-bold text-gray-700 mb-2">{title}</h2>
      <p className="text-gray-400 text-sm">Coming soon — we're working on this section.</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Suspense fallback={<Spinner />}>
          <Routes>

            {/* ── Public ── */}
            <Route element={<PublicLayout />}>
              <Route path="/"                          element={<HomePage />} />
              <Route path="/packages"                  element={<PackagesPage />} />
              <Route path="/package/:id"               element={<PackageDetailPage />} />
              <Route path="/enquiry/:id"               element={<EnquiryPage />} />
              <Route path="/destinations/domestic"     element={<DomesticDestinationsPage />} />
              <Route path="/destinations/international"element={<DomesticDestinationsPage />} />
              {/* Placeholder routes for nav links */}
              <Route path="/flights"   element={<ComingSoon title="Flights" />} />
              <Route path="/hotels"    element={<ComingSoon title="Hotels" />} />
              <Route path="/activity"  element={<ComingSoon title="Activities" />} />
              <Route path="/visa"      element={<ComingSoon title="Visa Services" />} />
              <Route path="/transfers" element={<ComingSoon title="Transfers" />} />
              <Route path="/cruise"    element={<ComingSoon title="Cruise" />} />
              <Route path="/gallery"   element={<ComingSoon title="Gallery" />} />
            </Route>

            {/* ── Admin login (no layout) ── */}
            <Route path="/login" element={<AdminLogin />} />

            {/* ── Protected admin routes ── */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard"    element={<AdminDashboard />} />
              <Route path="packages"     element={<AdminPackages />} />
              <Route path="itineraries"  element={<ItineraryMaker />} />
              <Route path="destinations" element={<AdminDestinations />} />
              <Route path="enquiries"    element={<AdminEnquiries />} />
              <Route path="settings"     element={<AdminSettings />} />
              {/* Default redirect inside admin */}
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>

            {/* ── Catch all ── */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </Suspense>
      </BrowserRouter>
    </AppProvider>
  );
}
